import { Circle } from '@chakra-ui/react'
import { useMatchRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { AccordionItem, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion'
import { Container } from '@/components/ui/container'
import { Separator } from '@/components/ui/separator'
import { HStack } from '@/components/ui/stack'
import useColorModeValue from '@/hooks/useColorModeValue'
import Citation from '@/pages/components/chat/components/citation'
import { ICitation as CitationProps } from '@/types/gigiChat/Conversation'

const Citations = ({ citations }: { citations: Array<CitationProps> }) => {
    const [isOpen, setIsOpen] = useState(false)
    const matchRoute = useMatchRoute()
    const dotsColor = useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)')
    if (!citations || !citations.length) {
        return null
    }

    const hasNoHiglitedCitations = () => {
        return citations.some((item) => item.usedInCompletion !== 1)
    }

    const areAllHiglitedOrNot = () => {
        //return true if all are highlited or non highlited since in this case we shouldn't display the accordion

        const allHightlited = citations.every((item) => item.usedInCompletion === 1)
        const allNonImportantHightlited = citations.every((item) => item.usedInCompletion !== 1)

        return allHightlited || allNonImportantHightlited
    }

    const getCitations = (show: boolean, maxNumberToRender?: number) => {
        if (!citations) {
            return null
        }
        return citations.map((item: CitationProps, index: number) => {
            if (maxNumberToRender && index > maxNumberToRender) return null
            if (!item.name) return false
            if (!item.url && !matchRoute({ to: '/documents' })) {
                const GSKSearch = 'https://search.gsk.com/app/EnterpriseSearchApp/#/search?query='
                const documentName = item.name.split('.')[0].replace(/_/g, ' ')
                const query = { name: 'enterprise-search-query', text: documentName, tab: 'all' }
                item.url = `${GSKSearch}${encodeURIComponent(JSON.stringify(query))}`
            }
            return (
                <Citation
                    text={item.name}
                    link={item.url}
                    key={item.name + index.toString()}
                    isHighlighted={item.usedInCompletion === 1}
                    isOpen={show}
                />
            )
        })
    }

    return (
        <>
            <Container variant="citations" maxWidth="unset" data-testid="chat-citations">
                <Separator variant="citations" />
                {areAllHiglitedOrNot() ? (
                    <HStack align="left" display="inline-block" mt="12px" width="100%">
                        <AccordionRoot multiple>
                            <AccordionItem value="highlighted" border="none" width="100%" display="flex">
                                <AccordionItemTrigger
                                    onClick={() => {
                                        citations.length > 2 && setIsOpen(!isOpen)
                                    }}
                                    _hover={{
                                        background: 'transparent',
                                    }}
                                    indicatorPlacement="none"
                                    alignItems="flex-start"
                                    flexWrap="wrap"
                                    p="0"
                                    cursor={citations.length > 2 ? ' pointer' : 'default'}
                                >
                                    {getCitations(true, !isOpen ? 1 : citations.length)}
                                    {!isOpen && citations.length > 2 ? (
                                        <HStack gap={1} justify="center" align="center" mt="4px">
                                            <Circle size="4px" bg={dotsColor} />
                                            <Circle size="4px" bg={dotsColor} />
                                            <Circle size="4px" bg={dotsColor} />
                                        </HStack>
                                    ) : (
                                        ''
                                    )}
                                </AccordionItemTrigger>
                            </AccordionItem>
                        </AccordionRoot>
                    </HStack>
                ) : (
                    <HStack align="left" display="inline-block" mt="12px" width="100%">
                        <AccordionRoot multiple>
                            <AccordionItem value="misc" border="none" width="100%" display="flex">
                                <AccordionItemTrigger
                                    onClick={() => {
                                        setIsOpen(!isOpen)
                                    }}
                                    _hover={{
                                        background: 'transparent',
                                    }}
                                    alignItems="flex-start"
                                    flexWrap="wrap"
                                    p="0"
                                >
                                    {getCitations(isOpen)}
                                    {!isOpen && hasNoHiglitedCitations() ? (
                                        <HStack gap={1} justify="center" align="center" mt="4px">
                                            <Circle size="4px" bg={dotsColor} />
                                            <Circle size="4px" bg={dotsColor} />
                                            <Circle size="4px" bg={dotsColor} />
                                        </HStack>
                                    ) : (
                                        ''
                                    )}
                                </AccordionItemTrigger>
                            </AccordionItem>
                        </AccordionRoot>
                    </HStack>
                )}
            </Container>
        </>
    )
}

export default Citations
